import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../../../services/API'
import { TenantSettingsContext } from '../../../../../App'
import { formatMoney } from '../../../../../helpers/currency'
import downloadFile from '../../../../../helpers/downloadFile'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DownloadIcon from '@mui/icons-material/Download'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { DataGrid } from '@mui/x-data-grid'

const PayRuns = ({ refreshKey, onChanged }) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [payRuns, setPayRuns] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [statusFilter, setStatusFilter] = useState('All')
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  })
  const [loading, setLoading] = useState(false)
  const [listError, setListError] = useState(null)
  const [selectedPayRun, setSelectedPayRun] = useState(null)
  const [loadingPayRunDetail, setLoadingPayRunDetail] = useState(false)
  // Error from mark-paid / export shown inside the detail dialog
  const [dialogError, setDialogError] = useState(null)
  const [deletePayRun, setDeletePayRun] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const [markPaidPayRun, setMarkPaidPayRun] = useState(null)
  const [exporting, setExporting] = useState(false)

  const currency = (value) => formatMoney(value, orgSettings?.currency)

  const loadPayRuns = () => {
    setLoading(true)
    API.get(
      `/payroll/pay-runs?status=${statusFilter}&page=${paginationModel.page}&pageSize=${paginationModel.pageSize}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setPayRuns(res.data.payRuns)
          setTotalCount(res.data.totalCount)
          setListError(null)
        } else {
          setListError(res.data?.error ?? 'Unable to load pay runs.')
        }
      })
      .catch(() => setListError('Unable to load pay runs.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadPayRuns()
  }, [refreshKey, statusFilter, paginationModel])

  const markPaid = (payRun) => {
    API.post(`/payroll/pay-runs/${payRun._id}/mark-paid`)
      .then((res) => {
        if (res.status === 200) {
          loadPayRuns()
          setMarkPaidPayRun(null)
          setSelectedPayRun(null)
          onChanged?.()
        } else {
          setMarkPaidPayRun(null)
          setDialogError(
            res.data?.error ??
              'Unable to mark this pay run as paid. Please try again.',
          )
        }
      })
      .catch(() => {
        setMarkPaidPayRun(null)
        setDialogError(
          'Unable to mark this pay run as paid. Please try again.',
        )
      })
  }

  const handleDeletePayRun = (payRun) => {
    if (!payRun?._id) return

    API.post(`/payroll/pay-runs/${payRun._id}/cancel`)
      .then((res) => {
        if (res.status === 200) {
          loadPayRuns()
          setSelectedPayRun(null)
          setDeletePayRun(null)
          setDeleteError(null)
          onChanged?.()
        } else {
          setDeleteError(
            res.data?.error ??
              'Unable to delete this pay run. Please try again.',
          )
        }
      })
      .catch(() =>
        setDeleteError('Unable to delete this pay run. Please try again.'),
      )
  }

  const openPayRun = (payRun) => {
    setLoadingPayRunDetail(true)
    setDialogError(null)
    setSelectedPayRun(payRun)
    API.get(`/payroll/pay-runs/${payRun._id}`)
      .then((res) => {
        if (res.status === 200) {
          setSelectedPayRun(res.data)
        } else {
          setDialogError(
            res.data?.error ?? 'Unable to load the pay run details.',
          )
        }
      })
      .catch(() => setDialogError('Unable to load the pay run details.'))
      .finally(() => setLoadingPayRunDetail(false))
  }

  const exportPayRun = async (payRun) => {
    if (!payRun?._id) return
    setExporting(true)
    setDialogError(null)
    const result = await downloadFile(
      `/payroll/pay-runs/${payRun._id}/export`,
      `${payRun.reference ?? 'pay-run'}.csv`,
    )
    if (!result.ok) {
      setDialogError(result.error ?? 'Unable to export this pay run.')
    }
    setExporting(false)
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack gap={2}>
        <Stack
          direction="row"
          gap={2}
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Stack>
            <Typography variant="title2">Pay Runs</Typography>
            <Typography variant="body2" color="text.secondary">
              Draft pay runs lock selected shifts until they are paid or cancelled.
            </Typography>
          </Stack>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setPaginationModel((prev) => ({ ...prev, page: 0 }))
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Paid">Paid</MenuItem>
          </TextField>
        </Stack>
        {loading && <LinearProgress />}
        {listError && (
          <Alert severity="error" onClose={() => setListError(null)}>
            {listError}
          </Alert>
        )}
        {!loading && totalCount === 0 && statusFilter === 'All' ? (
          <Alert severity="info" variant="outlined" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="body2" fontWeight={600}>
              No pay runs yet.
            </Typography>
            <Typography variant="body2">
              Pay runs group verified unpaid shifts into a payroll batch. Draft
              pay runs reserve the selected shifts while you review PAYE,
              invoice, and total pay figures, then marking the pay run as paid
              finalises those shifts and matches approved staff invoices.
            </Typography>
          </Alert>
        ) : (
          <Box sx={{ height: 320 }}>
            <DataGrid
              rows={payRuns}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              onRowClick={({ row }) => openPayRun(row)}
              pagination
              paginationMode="server"
              rowCount={totalCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[25, 50, 100]}
              columns={[
                {
                  field: 'reference',
                  headerName: 'Reference',
                  minWidth: 180,
                  flex: 1,
                },
                {
                  field: 'period',
                  headerName: 'Period',
                  minWidth: 220,
                  valueGetter: (value, row) =>
                    `${moment(row.periodStart).format('DD MMM YYYY')} - ${moment(
                      row.periodEnd,
                    ).format('DD MMM YYYY')}`,
                },
                {
                  field: 'status',
                  headerName: 'Status',
                  minWidth: 110,
                  renderCell: ({ row }) => (
                    <Chip
                      size="small"
                      color={row.status === 'Paid' ? 'success' : 'warning'}
                      label={row.status}
                    />
                  ),
                },
                {
                  field: 'shifts',
                  headerName: 'Shifts',
                  minWidth: 80,
                  valueGetter: (value, row) => row?.totals?.shifts ?? 0,
                },
                {
                  field: 'employees',
                  headerName: 'Employees',
                  minWidth: 110,
                  valueGetter: (value, row) => row?.totals?.employees ?? 0,
                },
                {
                  field: 'total',
                  headerName: 'Total',
                  minWidth: 120,
                  valueGetter: (value, row) => currency(row?.totals?.total),
                },
              ]}
            />
          </Box>
        )}
      </Stack>

      <Dialog
        open={!!selectedPayRun}
        onClose={() => setSelectedPayRun(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{selectedPayRun?.reference}</DialogTitle>
        <DialogContent dividers>
          {selectedPayRun && (
            <Stack gap={2}>
              {loadingPayRunDetail && <LinearProgress />}
              {dialogError && (
                <Alert severity="error" onClose={() => setDialogError(null)}>
                  {dialogError}
                </Alert>
              )}
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={selectedPayRun.status} />
                <Chip label={`${selectedPayRun?.totals?.shifts ?? 0} shifts`} />
                <Chip label={`${selectedPayRun?.totals?.employees ?? 0} employees`} />
                <Chip label={`PAYE ${currency(selectedPayRun?.totals?.paye)}`} />
                <Chip label={`Invoice ${currency(selectedPayRun?.totals?.invoice)}`} />
                {!!selectedPayRun?.totals?.unknown && (
                  <Chip label={`Unknown ${currency(selectedPayRun?.totals?.unknown)}`} />
                )}
                {!!selectedPayRun?.totals?.expenses && (
                  <Chip label={`Expenses ${currency(selectedPayRun?.totals?.expenses)}`} />
                )}
              </Stack>
              <Box sx={{ height: 420 }}>
                <DataGrid
                  rows={selectedPayRun.lines ?? []}
                  getRowId={(row) => row.shift}
                  disableRowSelectionOnClick
                  columns={[
                    {
                      field: 'employeeName',
                      headerName: 'Employee',
                      minWidth: 180,
                      flex: 1,
                    },
                    {
                      field: 'workerPaymentType',
                      headerName: 'Worker Type',
                      minWidth: 130,
                    },
                    {
                      field: 'hasInvoice',
                      headerName: 'Invoice',
                      minWidth: 90,
                      align: 'center',
                      headerAlign: 'center',
                      renderCell: ({ row }) =>
                        row.workerPaymentType === 'Invoice' && !row.hasInvoice ? (
                          <Tooltip title="No invoice was matched to this shift">
                            <Chip
                              size="small"
                              icon={<WarningAmberIcon />}
                              label="No Invoice"
                              color="warning"
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          </Tooltip>
                        ) : (
                          ''
                        ),
                    },
                    {
                      field: 'start',
                      headerName: 'Shift',
                      minWidth: 170,
                      valueGetter: (value) =>
                        moment(value).format('DD MMM YYYY HH:mm'),
                    },
                    {
                      field: 'description',
                      headerName: 'Description',
                      minWidth: 160,
                      flex: 1,
                    },
                    {
                      field: 'hours',
                      headerName: 'Hours',
                      minWidth: 90,
                    },
                    {
                      field: 'bonusPay',
                      headerName: 'Bonus',
                      minWidth: 100,
                      valueGetter: (value) => currency(value ?? 0),
                    },
                    {
                      field: 'travelPay',
                      headerName: 'Travel',
                      minWidth: 100,
                      valueGetter: (value) => currency(value ?? 0),
                    },
                    {
                      field: 'totalPay',
                      headerName: 'Total',
                      minWidth: 110,
                      valueGetter: (value) => currency(value),
                    },
                  ]}
                />
              </Box>
              {!!selectedPayRun?.expenseLines?.length && (
                <Stack gap={1}>
                  <Typography variant="title3">Expenses</Typography>
                  <Box sx={{ height: 260 }}>
                    <DataGrid
                      rows={selectedPayRun.expenseLines}
                      getRowId={(row) => row.expense}
                      disableRowSelectionOnClick
                      columns={[
                        {
                          field: 'employeeName',
                          headerName: 'Employee',
                          minWidth: 180,
                          flex: 1,
                        },
                        {
                          field: 'category',
                          headerName: 'Category',
                          minWidth: 130,
                        },
                        {
                          field: 'description',
                          headerName: 'Description',
                          minWidth: 190,
                          flex: 1,
                        },
                        {
                          field: 'expenseDate',
                          headerName: 'Date',
                          minWidth: 130,
                          valueGetter: (value) =>
                            value ? moment(value).format('DD MMM YYYY') : '',
                        },
                        {
                          field: 'amount',
                          headerName: 'Amount',
                          minWidth: 120,
                          valueGetter: (value) => currency(value),
                        },
                      ]}
                    />
                  </Box>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedPayRun(null)}>Close</Button>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => exportPayRun(selectedPayRun)}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          {selectedPayRun?.status === 'Draft' && (
            <>
              <Button color="error" onClick={() => setDeletePayRun(selectedPayRun)}>
                Delete Draft Pay Run
              </Button>
              <Button
                variant="contained"
                onClick={() => setMarkPaidPayRun(selectedPayRun)}
              >
                Mark Paid
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!markPaidPayRun}
        onClose={() => setMarkPaidPayRun(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Mark pay run as paid?</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            <Typography variant="body2">
              This will mark every shift and expense in{' '}
              {markPaidPayRun?.reference} as paid and match any approved staff
              invoices covering its shifts.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This cannot be undone.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidPayRun(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => markPaid(markPaidPayRun)}
          >
            Mark Paid
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deletePayRun}
        onClose={() => {
          setDeletePayRun(null)
          setDeleteError(null)
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete draft pay run?</DialogTitle>
        <DialogContent>
          <Stack gap={1}>
            <Typography variant="body2">
              This will remove {deletePayRun?.reference} from active pay runs and
              put its shifts and expenses back onto the unpaid payroll lists.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Paid pay runs cannot be deleted.
            </Typography>
            {deleteError && (
              <Alert severity="error" onClose={() => setDeleteError(null)}>
                {deleteError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeletePayRun(null)
              setDeleteError(null)
            }}
          >
            Keep Pay Run
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDeletePayRun(deletePayRun)}
          >
            Delete Pay Run
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default PayRuns
